// MaturaTrapezoidAreasClosedTask.js
// Zadanie maturalne – zamknięte.
// W trapezie ABCD o podstawach AB i CD (AB = k * CD, k ∈ {2,3}) przekątne przecinają się w E.
// Zdanie o BCE i AED: zawsze prawdziwe.
// Zdanie o ABE i CDE: pułapka k vs k^2, ale bez trywialnych sprzeczności „na oko”.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import MaturaTrapezoidAreasDiagram from "../../../../components/diagrams/MaturaTrapezoidAreasDiagram";
import TruthInput from "../../../../components/inputs/TruthInput";

// --------------------------------------------------
// GENERATOR
// --------------------------------------------------

function createOriginalTask() {
  return {
    type: "MaturaTrapezoidAreasClosedTask",
    data: {
      k: 2,
      trueRatio: 4,
      num: 2,
      direction: "większe",
      swapTriangles: false,
      orderSwap: false,
      correctPF: ["P", "F"],
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 23,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  const kOptions = [2, 3];
  const k = kOptions[Math.floor(Math.random() * kOptions.length)];
  const trueRatio = k * k; // k^2 (4 lub 9)

  // W zdaniu o ABE i CDE wybieramy, czy po lewej stronie stoi większy czy mniejszy trójkąt:
  // swapTriangles=false -> A=ABE (większy), B=CDE (mniejszy) -> "większe"
  // swapTriangles=true  -> A=CDE (mniejszy), B=ABE (większy) -> "mniejsze"
  const swapTriangles = Math.random() < 0.5;
  const direction = swapTriangles ? "mniejsze" : "większe";

  // Pułapka: uczeń myli k z k^2, więc w treści pojawia się tylko k albo k^2.
  const num = Math.random() < 0.5 ? k : trueRatio;

  // Zdanie o ABE i CDE jest prawdziwe wtedy i tylko wtedy, gdy w treści stoi k^2.
  // (dla "mniejsze" interpretacja: "num razy mniejsze" = 1/num, ale liczba nadal musi być k^2)
  const isCorrect = num === trueRatio;

  // Losujemy kolejność zdań (zdanie o ABE/CDE oraz zdanie o BCE/AED)
  const orderSwap = Math.random() < 0.5;

  // Zdanie o BCE/AED jest zawsze P.
  // correctPF odpowiada kolejności wyświetlania.
  const correctPF = orderSwap
    ? [isCorrect ? "P" : "F", "P"]
    : ["P", isCorrect ? "P" : "F"];

  return {
    type: "MaturaTrapezoidAreasClosedTask",
    data: {
      k,
      trueRatio,
      num,
      direction,
      swapTriangles,
      orderSwap,
      correctPF,
    },
  };
}

// --------------------------------------------------
// TREŚĆ
// --------------------------------------------------

export function renderTask({ data }) {
  const { k, num, direction, swapTriangles, orderSwap } = data;
  const abEqLatex = `|AB| = ${k} \\cdot |CD|`;

  const T1 = <InlineMath math={'ABE'} />; // większy
  const T2 = <InlineMath math={'CDE'} />; // mniejszy

  const A = swapTriangles ? T2 : T1;
  const B = swapTriangles ? T1 : T2;

  const sentenceAboutEqualAreas = (
    <>
      Pola trójkątów <InlineMath math={'BCE'} /> oraz <InlineMath math={'AED'} /> są równe.
    </>
  );

  const sentenceAboutRatio = (
    <>
      Pole trójkąta {A} jest <InlineMath math={String(num)} /> razy {direction} od pola trójkąta {B}.
    </>
  );

  const sentences = orderSwap
    ? [sentenceAboutRatio, sentenceAboutEqualAreas]
    : [sentenceAboutEqualAreas, sentenceAboutRatio];

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>

      <p>
        Dany jest trapez <InlineMath math={'ABCD'} /> o podstawach <InlineMath math={'AB'} /> i{" "}
        <InlineMath math={'CD'} />,{" "}
        <span style={{ whiteSpace: "nowrap" }}>
          dla którego zachodzi <InlineMath math={abEqLatex} />.
        </span>{" "}
        Przekątne <InlineMath math={'AC'} /> i <InlineMath math={'BD'} /> przecinają się w punkcie{" "}
        <InlineMath math={'E'} /> (zobacz rysunek).
      </p>

      <div
        style={{
          marginTop: -20,
          marginBottom: -10,
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div style={{ width: "min(100%, 380px)" }}>
          <MaturaTrapezoidAreasDiagram size={380} k={k} />
        </div>
      </div>

      <p>Oceń prawdziwość poniższych zdań:</p>

      <p><InlineMath math="1." /> {sentences[0]}</p>
      <p><InlineMath math="2." /> {sentences[1]}</p>
    </div>
  );
}

// --------------------------------------------------
// INPUT
// --------------------------------------------------

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const value = userAnswer || {};

  const setValue = (key, v) => {
    onChange?.({
      ...value,
      [key]: v,
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>1.</span>
        <TruthInput
          value={value.s1 || null}
          disabled={disabled}
          onChange={(v) => setValue("s1", v)}
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>2.</span>
        <TruthInput
          value={value.s2 || null}
          disabled={disabled}
          onChange={(v) => setValue("s2", v)}
        />
      </div>
    </div>
  );
}

// --------------------------------------------------
// CHECK
// --------------------------------------------------

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;
  const { correctPF } = data;
  const { s1, s2 } = userAnswer;
  return s1 === correctPF[0] && s2 === correctPF[1];
}

// --------------------------------------------------
// POPRAWNA ODPOWIEDŹ
// --------------------------------------------------

export function renderAnswer({ data }) {
  const { correctPF } = data;

  const s1Latex = `\\text{${correctPF[0]}}`;
  const s2Latex = `\\text{${correctPF[1]}}`;

  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <p>
        <InlineMath math="1." /> <InlineMath math={s1Latex} />
      </p>
      <p>
        <InlineMath math="2." /> <InlineMath math={s2Latex} />
      </p>
    </div>
  );
}

// --------------------------------------------------
// ODPOWIEDŹ UŻYTKOWNIKA
// --------------------------------------------------

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) {
    return <i>(brak odpowiedzi)</i>;
  }

  const s1 = userAnswer.s1 || "-";
  const s2 = userAnswer.s2 || "-";

  const s1Latex = s1 === "-" ? "-" : `\\text{${s1}}`;
  const s2Latex = s2 === "-" ? "-" : `\\text{${s2}}`;

  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <p>
        1. <InlineMath math={s1Latex} />
      </p>
      <p>
        2. <InlineMath math={s2Latex} />
      </p>
    </div>
  );
}

// --------------------------------------------------
// WYJAŚNIENIE W KROKACH (dopasowane do kolejności zdań)
// --------------------------------------------------

export function renderExplanation({ data }) {
  const { k, trueRatio, num, swapTriangles, orderSwap, correctPF } = data;

  // Zdanie o stosunku pól (ABE/CDE)
  const leftTriangle = swapTriangles ? "CDE" : "ABE";
  const rightTriangle = swapTriangles ? "ABE" : "CDE";

  // Które zdanie jest na ekranie jako 1., a które jako 2.
  const s1Type = orderSwap ? "ratio" : "equal";
  const s2Type = orderSwap ? "equal" : "ratio";

  const renderEqual = (idx) => (
    <>
      <p>
        <strong>Krok {idx}.</strong> Zdanie <InlineMath math={String(idx)} />: <InlineMath math={'P(BCE)=P(AED)'} />.
      </p>

      <p>
        Trójkąty <InlineMath math="ABC" /> i <InlineMath math="ABD" /> mają wspólną podstawę <InlineMath math="AB" /> i równe wysokości, ponieważ <InlineMath math={"AB\\parallel CD"} />. Po odjęciu od ich równych pól pola wspólnego trójkąta <InlineMath math="ABE" /> otrzymujemy:
      </p>

      <BlockMath math={'P(BCE)=P(AED)'} />

      <p>
        Wniosek: zdanie <InlineMath math={String(idx)} /> jest <strong>{correctPF[idx - 1] === "P" ? "prawdziwe" : "fałszywe"}</strong>.
      </p>
    </>
  );

  const renderRatio = (idx) => {
    const isTrue = correctPF[idx - 1] === "P";

    return (
      <>
        <p>
          <strong>Krok {idx}.</strong> Zdanie <InlineMath math={String(idx)} />: porównanie pól <InlineMath math={leftTriangle} /> i{" "}
          <InlineMath math={rightTriangle} />.
        </p>

        <p>
          Trójkąty <InlineMath math="ABE" /> i <InlineMath math="CDE" /> są podobne. Stosunek odpowiadających boków wynosi <InlineMath math={`\\frac{|AB|}{|CD|}=${k}`} />, więc stosunek pól jest kwadratem tej liczby:
        </p>

        <BlockMath math={`\\dfrac{P(ABE)}{P(CDE)}=${k}^2=${trueRatio}`} />
        {swapTriangles && <BlockMath math={`\\dfrac{P(CDE)}{P(ABE)}=\\frac{1}{${trueRatio}}`} />}

        <p>
          Pole trójkąta <InlineMath math={leftTriangle} /> jest <InlineMath math={String(trueRatio)} /> razy {swapTriangles ? "mniejsze" : "większe"} od pola trójkąta <InlineMath math={rightTriangle} />.
        </p>

        {isTrue ? (
          <p>
            W treści jest liczba <InlineMath math={String(num)} />, czyli zgadza się z <InlineMath math={String(trueRatio)} />.
            Wniosek: zdanie <InlineMath math={String(idx)} /> jest <strong>prawdziwe</strong>.
          </p>
        ) : (
          <p>
            W treści jest liczba <InlineMath math={String(num)} />, ale powinno być <InlineMath math={String(trueRatio)} />.
            Wniosek: zdanie <InlineMath math={String(idx)} /> jest <strong>fałszywe</strong>.
          </p>
        )}
      </>
    );
  };

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      {s1Type === "equal" ? renderEqual(1) : renderRatio(1)}
      {s2Type === "equal" ? renderEqual(2) : renderRatio(2)}
    </div>
  );
}

